import { IGenericJSON } from '../helpers/interface';

export type IntegrationType = 'API' | 'CSV';
export type IntegrationStatus = 'CONNECTED' | 'DISCONNECTED' | 'ERROR' | 'PENDING';

export interface ISyncHistoryEntry {
  success: boolean;
  trigger: string;
  date: Date | null;
}

export interface IImportedDataSummary {
  schools: number;
  faculty: number;
  students: number;
}

export class IntegrationCredentials {
  id: number;
  integrationId: number;
  clientId: string | null;
  clientSecret: string | null;
  createdAt: Date | null;
  updatedAt: Date | null;

  constructor(data: IGenericJSON) {
    this.id = data['id'];
    this.integrationId = data['integration_id'];
    this.clientId = data['client_id'] ?? null;
    this.clientSecret = data['encrypted_client_secret'] ?? null;
    this.createdAt = data['created_at'] ? new Date(data['created_at']) : null;
    this.updatedAt = data['updated_at'] ? new Date(data['updated_at']) : null;
  }
}

export class Integration {
  id: number;
  districtId: number;
  type: IntegrationType;
  provider: string | null;
  baseUrl: string | null;
  syncFrequency: string | null;
  automaticSync: boolean | true;
  status: IntegrationStatus;
  lastSuccessfulSync: Date | null;
  lastAttempt: Date | null;
  lastError: string | null;
  createdAt: Date | null;
  updatedAt: Date | null;

  credentials: IntegrationCredentials | null;
  syncHistory: ISyncHistoryEntry[];
  importedData: IImportedDataSummary | null;

  constructor(data: IGenericJSON = {}) {
    this.id = data['id'];
    this.districtId = data['district_id'];
    this.type = data['type'] ?? null;
    this.provider = data['provider'] ?? null;
    this.baseUrl = data['base_url'] ?? null;
    this.status = data['status'] ?? null;
    this.syncFrequency = data['sync_frequency'] ?? null;
    this.automaticSync = data['automatic_sync'] ?? true;
    this.lastSuccessfulSync = data['last_successful_sync']
      ? new Date(data['last_successful_sync'])
      : null;
    this.lastAttempt = data['last_attempt'] ? new Date(data['last_attempt']) : null;
    this.lastError = data['last_error'] ?? null;
    this.createdAt = data['created_at'] ? new Date(data['created_at']) : null;
    this.updatedAt = data['updated_at'] ? new Date(data['updated_at']) : null;

    this.credentials = data['credentials'] ? new IntegrationCredentials(data['credentials']) : null;

    this.syncHistory = (data['sync_history'] ?? []).map((run: IGenericJSON) => ({
      success: run['success'],
      trigger: run['trigger'],
      date: run['created_at'] ? new Date(run['created_at']) : null,
    }));

    this.importedData = data['imported_data']
      ? {
          schools: data['imported_data']['schools'] ?? 0,
          faculty: data['imported_data']['faculty'] ?? 0,
          students: data['imported_data']['students'] ?? 0,
        }
      : null;
  }

  public static digest(data: IGenericJSON): Integration {
    return new Integration(data);
  }

  public static digestResponse(data: IGenericJSON[]): Integration[] {
    const ret: Integration[] = [];

    data.forEach((u: IGenericJSON): void => {
      ret.push(this.digest(u));
    });

    return ret;
  }
}
